import { TextField } from "@mui/material";
import { PhoneNumberTextMask, InputSelectAdornment } from "./components";
import { useState, useRef, useMemo } from "react";
import { isUSPhoneNumberFormat, generateHandleChangeFormat } from "./helpers";
import { OnChange, Format, OnChangeEvent, PhoneNumberInputProps, ReadOnlyPhoneNumberInputProps } from "./types";
import { labelOffsetX, MuiLabelOffsetX, MuiShrinkLabelOffsetY, MuiShrinkLabelScale, MuiLabelOffsetY, MuiLabelScale, MuiLabelOffsetYSmall } from "./constants";

const PhoneNumberInput = <T extends OnChange>({ value, onChange, onFocus, onBlur, size, disabled, ...textFieldProps }: PhoneNumberInputProps<T>) => {
  const defaultFormat: Format = value ? (isUSPhoneNumberFormat(value) ? "US" : "INTL") : "US";

  // ** State
  const [format, setFormat] = useState<Format>(defaultFormat);
  const [inputLabelShrink, setInputLabelShrink] = useState<boolean>(value ? true : false);

  // ** Refs
  const TextFieldRef = useRef<HTMLInputElement>(null);

  const readOnly = (textFieldProps as ReadOnlyPhoneNumberInputProps<T>).readOnly || false;

  const inputLabelShrinkValue = useMemo(() => {
    if (value) return true;

    return inputLabelShrink;
  }, [inputLabelShrink, value]);

  // ** Methods/handlers

  // Add validation to try to keep the value a valid phone number and replace invalid characters
  const customOnChange = useMemo(() => {
    return (event: OnChangeEvent) => {
      const { value } = event.target;

      // Replace any character that is not a number, parenthesis, dash, space, plus, or period with an empty string
      const formattedValue = value.replace(/[^\d()\-\s+.]/g, "");

      event.target.value = formattedValue;

      onChange && onChange(event);
    };
  }, [onChange]);

  const customOnFocus = useMemo(() => {
    return (event: any) => {
      if (!inputLabelShrink) setInputLabelShrink(true);

      onFocus && onFocus(event);
    };
  }, [onFocus]);

  const customOnBlur = useMemo(() => {
    return (event: any) => {
      if (!value) setInputLabelShrink(false);

      onBlur && onBlur(event);
    };
  }, [onBlur, value]);

  // ** Styles
  const inputLabelTransform: string = useMemo(() => {
    // Handle placement of input label depending on inputLabelShrink and size of the component
    const defaultTransform = `translate(${labelOffsetX}px, ${MuiLabelOffsetY}px) scale(${MuiLabelScale})`;
    const shrinkTransform = `translate(${labelOffsetX}px, ${MuiShrinkLabelOffsetY}px) scale(${MuiShrinkLabelScale})`;
    const smallTransform = `translate(${labelOffsetX}px, ${MuiLabelOffsetYSmall}px) scale(${MuiLabelScale})`;

    if (inputLabelShrinkValue) return shrinkTransform;

    return size === "small" ? smallTransform : defaultTransform;
  }, [inputLabelShrinkValue, size]);

  const handleChangeFormat = generateHandleChangeFormat({ value, onChange: customOnChange, TextFieldRef, setFormat });

  return (
    <TextField
      {...textFieldProps}
      ref={TextFieldRef}
      sx={{
        ".MuiInputBase-root": {
          paddingLeft: "0px !important",
        },
        // Places the legend above the Text instead of above the Select (when shrink)
        "& legend": {
          marginLeft: inputLabelShrink ? `${labelOffsetX - MuiLabelOffsetX}px` : "0px",
        },
        ...textFieldProps.sx,
      }}
      value={value || ""}
      onChange={customOnChange}
      InputLabelProps={{
        ...textFieldProps.InputLabelProps,
        sx: {
          ...(textFieldProps.InputLabelProps?.sx || {}),
          // Places the legend above the Text instead of above the Select depending on input label shrink
          transform: inputLabelTransform,
        },
        shrink: inputLabelShrinkValue,
      }}
      InputProps={{
        ...textFieldProps.InputProps,
        startAdornment: (
          <>
            {/* Format Select component as a start adornment of the input */}
            <InputSelectAdornment value={format} onChange={handleChangeFormat} readOnly={readOnly} disabled={disabled} size={size} />
            {textFieldProps.InputProps?.startAdornment}
          </>
        ),
        inputComponent: format === "US" ? (PhoneNumberTextMask as any) : undefined,
        readOnly: readOnly || disabled,
      }}
      // Used to manage shrink prop manually
      onFocus={!disabled ? customOnFocus : undefined}
      onBlur={!disabled ? customOnBlur : undefined}
      size={size}
      disabled={disabled}
    />
  );
};

export default PhoneNumberInput;
